import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import { IoChatbubbleEllipsesOutline, IoLanguage, IoPlay, IoBrushOutline } from "react-icons/io5";
import { useAuth } from "../../services/AuthContext";
import { normalizeLanguage } from "../../services/language";
import ModernSidebar, { useModernRouteActions } from "../../components/lobby/ModernSidebar";
import { DIRECT_MESSAGE_PANEL_OPEN_EVENT } from "../../services/directMessageUi";
import "../../components/lobby/lobbyModern.css";
import HistoryProgressChart, { PROGRESS_LINES } from "./HistoryProgressChart";
import "./historyModern.css";

// Historial de partidas con el diseño Clásico (lobby plano): cifras reales arriba,
// filtros y tabla paginada que vienen ya filtrados del servidor.
const PAGE_SIZE = 100;
const RESULTS = [
  ["", "Todas"],
  ["win", "🏆 Ganadas"],
  ["loss", "✕ Perdidas"],
  ["draw", "= Empates"],
];
const MODES = [
  ["science", "Ciencia"],
  ["cienzar", "Cienzar"],
  ["contrazar", "Contrazar"],
  ["chance", "Azar"],
];
const MODE_OF = (mode) => {
  const value = String(mode || "").toLowerCase();
  if (value.endsWith("cienzar")) return ["cienzar", "Cienzar"];
  if (value.endsWith("sciencevsazar") || value.endsWith("contrazar")) return ["contrazar", "Contrazar"];
  if (value.endsWith("dice")) return ["chance", "Azar"];
  if (value === "freebox") return ["science", "Caja de arena"];
  return ["science", "Ciencia"];
};
const RESULT_LABEL = { win: "🏆 Victoria", loss: "✕ Derrota", draw: "= Empate" };
const STREAK_LABEL = { win: "victorias", loss: "derrotas", draw: "empates" };
const MODE_TILES = [
  { key: "science", field: "ciencia", label: "Ciencia", icon: "🔬" },
  { key: "cienzar", field: "cienzar", label: "Cienzar", icon: "🧪" },
  { key: "contrazar", field: "sciencevsazar", label: "Contrazar", icon: "⚔️" },
  { key: "chance", field: "azar", label: "Azar", icon: "🎲" },
];
const RANGES = [
  ["7", "7 d"],
  ["30", "30 d"],
  ["all", "Todo"],
];
const RANGE_TITLE = { 7: "últimos 7 días", 30: "últimos 30 días", all: "desde el principio" };

const formatNumber = (value) => Number(value || 0).toLocaleString("es-ES");
const percent = (part, total) => (total > 0 ? `${Math.round((part / total) * 100)} %` : "0 %");
const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};
const formatWhen = (value) => {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime()) || !date.getTime()) return "—";
  const time = date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 3600 * 1000);
  if (date.toDateString() === today.toDateString()) return `Hoy ${time}`;
  if (date.toDateString() === yesterday.toDateString()) return `Ayer ${time}`;
  return `${date.toLocaleDateString("es-ES", { day: "numeric", month: "short" })} ${time}`;
};
const initialsOf = (name) =>
  String(name || "?")
    .replace(/[^\p{L}\p{N} ]/gu, "")
    .trim()
    .slice(0, 1)
    .toUpperCase() || "?";

const HistoryModern = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language);
  const { user } = useAuth() || {};
  const routeActions = useModernRouteActions();
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState({ matches: [], total: 0, totalPages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState("");
  const [mode, setMode] = useState("");
  const [rivalInput, setRivalInput] = useState("");
  const [rival, setRival] = useState("");
  const [days, setDays] = useState("");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const rowsScrollRef = useRef(null);
  useEffect(() => {
    if (rowsScrollRef.current) rowsScrollRef.current.scrollTop = 0;
  }, [page]);
  const [range, setRange] = useState("30");
  const [progress, setProgress] = useState(null);
  const [hiddenLines, setHiddenLines] = useState({});

  // Con este diseño el menú es el propio; el de la app se oculta en el ordenador.
  useEffect(() => {
    document.body.classList.add("xima-lobby-modern-active");
    return () => document.body.classList.remove("xima-lobby-modern-active");
  }, []);

  useEffect(() => {
    axios
      .get("/tournaments/history/summary")
      .then((response) => setSummary(response?.data || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let canceled = false;
    axios
      .get("/tournaments/history/progress", { params: { range } })
      .then((response) => {
        if (!canceled) setProgress(response?.data || null);
      })
      .catch(() => {});
    return () => {
      canceled = true;
    };
  }, [range]);

  useEffect(() => {
    const timer = window.setTimeout(() => setRival(rivalInput.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [rivalInput]);

  useEffect(() => {
    setPage(1);
  }, [result, mode, rival, days, sort]);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    axios
      .get("/tournaments/history/list", {
        params: { page, pageSize: PAGE_SIZE, result, mode, rival, days, sort },
      })
      .then((response) => {
        if (!canceled) setList(response?.data || { matches: [], total: 0, totalPages: 1, page: 1 });
      })
      .catch(() => {})
      .finally(() => {
        if (!canceled) setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [page, result, mode, rival, days, sort]);

  const openReplay = (match, analyze = false) => {
    navigate("/game/history", {
      state: {
        replayReturnTo: "/game/history",
        openReplayRequestKey: `history:${match.id}:${Date.now()}`,
        openReplayMatch: {
          id: match.id,
          historyId: match.id,
          tournamentName: match.tournamentName || "Partida",
          mode: match.mode,
          modeLabel: MODE_OF(match.mode)[1],
          hasReplay: true,
          replaySteps: match.replaySteps,
          participants: match.participants,
          analyze,
        },
      },
    });
  };

  const total = Number(summary?.total) || 0;
  const pages = useMemo(() => {
    const totalPages = Math.max(1, Number(list.totalPages) || 1);
    const current = Math.min(page, totalPages);
    const set = new Set([1, 2, 3, current - 1, current, current + 1, totalPages].filter((n) => n >= 1 && n <= totalPages));
    const sorted = [...set].sort((a, b) => a - b);
    const output = [];
    sorted.forEach((n, index) => {
      if (index > 0 && n - sorted[index - 1] > 1) output.push("…");
      output.push(n);
    });
    return output;
  }, [list.totalPages, page]);
  const firstShown = list.total ? (Math.min(page, list.totalPages) - 1) * PAGE_SIZE + 1 : 0;
  const lastShown = Math.min(list.total, firstShown + list.matches.length - 1);
  const userName = user?.username || user?.loginIdentifier || user?.email || "";
  const badges = outletContext.sidebarBadges || {};

  return (
    <div className="xlm-root xhm-root">
      <ModernSidebar
        language={language}
        active="history"
        badges={badges}
        isAdmin={Boolean(outletContext.hasAdminMenuAccess)}
        actions={routeActions}
      />
      <div className="xlm-main">
        <header className="xlm-topbar">
          <div className="xlm-heading">
            <span>Tus partidas, de la más reciente a la más antigua</span>
            <h1>Historial de partidas</h1>
          </div>
          <div />
          <div className="xlm-top-actions">
            <button type="button" className="xlm-round" onClick={routeActions.design} title="Modo Diseñar">
              <IoBrushOutline />
            </button>
            <button
              type="button"
              className="xlm-round"
              onClick={() => window.dispatchEvent(new CustomEvent(DIRECT_MESSAGE_PANEL_OPEN_EVENT))}
              title="Mensajes"
            >
              <IoChatbubbleEllipsesOutline />
            </button>
            <button type="button" className="xlm-round" onClick={routeActions.language} title="Idioma">
              <IoLanguage />
            </button>
            <span className="xlm-profile">
              <span className="xlm-profile-avatar">{initialsOf(userName)}</span>
              <strong>{userName}</strong>
              <em>{summary ? `${formatNumber(summary.cimaxTotal)} CIMAX` : ""}</em>
            </span>
          </div>
        </header>

        <p className="xhm-summary-line">
          {summary
            ? `${formatNumber(total)} partidas · ${formatNumber(summary.wins)} ganadas (${percent(summary.wins, total)}) · ${formatNumber(summary.losses)} perdidas · ${formatNumber(summary.draws)} empates · racha actual: ${summary.streak?.count || 0} ${STREAK_LABEL[summary.streak?.type] || ""}`
            : "Cargando tus números…"}
        </p>

        <section className="xhm-top">
          <div className="xhm-chart-card">
            <div className="xhm-chart-head">
              <div>
                <span>CIMAX · {RANGE_TITLE[range]}</span>
                <b>
                  {progress ? formatNumber(progress.totalNow) : "…"}
                  {progress ? (
                    <em className={progress.totalChange >= 0 ? "is-up" : "is-down"}>
                      {progress.totalChange >= 0 ? " ▲ +" : " ▼ "}
                      {formatNumber(progress.totalChange)}
                    </em>
                  ) : null}
                </b>
              </div>
              <div className="xhm-range">
                {RANGES.map(([value, label]) => (
                  <button key={value} type="button" className={range === value ? "is-active" : ""} onClick={() => setRange(value)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <HistoryProgressChart series={progress?.series || {}} hidden={hiddenLines} />
            <div className="xhm-legend">
              {PROGRESS_LINES.map((line) => {
                const points = progress?.series?.[line.field] || [];
                const change = points.length ? points[points.length - 1].v - points[0].v : 0;
                return (
                  <button
                    key={line.field}
                    type="button"
                    className={hiddenLines[line.field] ? "is-off" : ""}
                    onClick={() => setHiddenLines((current) => ({ ...current, [line.field]: !current[line.field] }))}
                  >
                    <i style={{ background: line.color }} /> {line.label}
                    <small className={change >= 0 ? "is-up" : "is-down"}>
                      {change >= 0 ? "+" : ""}
                      {formatNumber(change)}
                    </small>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="xhm-tiles">
            {MODE_TILES.map((tile) => {
              const stats = progress?.modes?.[tile.key] || { games: 0, wins: 0, losses: 0, cimax: 0 };
              return (
                <button
                  key={tile.key}
                  type="button"
                  className={`xhm-tile is-${tile.key}${mode === tile.key ? " is-selected" : ""}`}
                  onClick={() => setMode((current) => (current === tile.key ? "" : tile.key))}
                  title="Ver solo las partidas de este modo"
                >
                  <span className="xhm-tile-head">
                    <strong>{tile.label}</strong>
                    <span aria-hidden="true">{tile.icon}</span>
                  </span>
                  <b>{progress ? percent(stats.wins, stats.games) : "…"}</b>
                  <small>
                    {formatNumber(stats.wins)} G · {formatNumber(stats.losses)} P · CIMAX {formatNumber(stats.cimax)}
                  </small>
                </button>
              );
            })}
          </div>
        </section>

        <section className="xhm-filters">
          {RESULTS.map(([value, label]) => (
            <button key={value || "all"} type="button" className={result === value ? "is-active" : ""} onClick={() => setResult(value)}>
              {label}
            </button>
          ))}
          <i className="xhm-sep" />
          {MODES.map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={mode === value ? "is-active" : ""}
              onClick={() => setMode((current) => (current === value ? "" : value))}
            >
              <span className={`xhm-dot is-${value}`} /> {label}
            </button>
          ))}
          <span className="xhm-filters-right">
            <input
              type="search"
              placeholder="🔍 Buscar rival…"
              value={rivalInput}
              onChange={(event) => setRivalInput(event.target.value)}
            />
            <select value={days} onChange={(event) => setDays(event.target.value)}>
              <option value="">📅 Todas las fechas</option>
              <option value="7">📅 Últimos 7 días</option>
              <option value="30">📅 Últimos 30 días</option>
              <option value="90">📅 Últimos 90 días</option>
            </select>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="recent">Más recientes</option>
              <option value="oldest">Más antiguas</option>
            </select>
          </span>
        </section>

        <section className="xhm-table-card">
          <div className="xhm-row is-head">
            <span>RESULTADO</span>
            <span>MODO</span>
            <span>RIVAL</span>
            <span>JUGADAS</span>
            <span>TU FUERZA</span>
            <span>FUERZA RIVAL</span>
            <span>CIMAX</span>
            <span>DURACIÓN</span>
            <span>FECHA</span>
            <span>VER</span>
          </div>
          {/* 100 partidas por página: se desplazan dentro de la tabla, con la cabecera fija. */}
          <div className="xhm-rows-scroll" ref={rowsScrollRef}>
          {list.matches.map((match) => {
            const [modeKey, modeLabel] = MODE_OF(match.mode);
            const rivalEntry = match.rivals?.[0] || {};
            const extraRivals = Math.max(0, (match.rivals?.length || 0) - 1);
            return (
              <div key={match.id} className="xhm-row">
                <span>
                  <em className={`xhm-result is-${match.outcome}`}>{RESULT_LABEL[match.outcome]}</em>
                </span>
                <span className="xhm-mode">
                  <i className={`xhm-dot is-${modeKey}`} /> {modeLabel}
                </span>
                <span className="xhm-rival">
                  <b className="xhm-avatar">{initialsOf(rivalEntry.displayName)}</b>
                  <span>
                    <strong>
                      {rivalEntry.displayName || "—"}
                      {extraRivals ? ` +${extraRivals}` : ""}
                    </strong>
                    <small>
                      {rivalEntry.isBot ? "Bot" : rivalEntry.cimax != null ? `CIMAX ${formatNumber(rivalEntry.cimax)}` : ""}
                    </small>
                  </span>
                </span>
                <span className="xhm-num">{formatNumber(match.totalMoves)}</span>
                <span className="xhm-num">{formatNumber(match.myForce)}</span>
                <span className="xhm-num">{formatNumber(match.rivalForce)}</span>
                <span className={`xhm-num ${Number(match.cimaxDelta) > 0 ? "is-up" : Number(match.cimaxDelta) < 0 ? "is-down" : ""}`}>
                  {match.cimaxDelta == null ? "—" : `${match.cimaxDelta > 0 ? "+" : match.cimaxDelta === 0 ? "±" : ""}${match.cimaxDelta}`}
                </span>
                <span>{formatDuration(match.durationSeconds)}</span>
                <span className="xhm-when">{formatWhen(match.endedAt)}</span>
                <span className="xhm-actions">
                  <button
                    type="button"
                    className="xhm-analyze"
                    disabled={!match.hasReplay}
                    onClick={() => openReplay(match, true)}
                    title={match.hasReplay ? "El bot revisa cada jugada de esta partida" : "Esta partida no tiene repetición"}
                  >
                    ★ Analizar partida
                  </button>
                  <button
                    type="button"
                    className="xhm-play"
                    disabled={!match.hasReplay}
                    onClick={() => openReplay(match)}
                    title={match.hasReplay ? "Reproducir partido" : "Esta partida no tiene repetición"}
                  >
                    <IoPlay /> Reproducir partido
                  </button>
                </span>
              </div>
            );
          })}
          {!loading && list.matches.length === 0 ? (
            <div className="xhm-empty">No hay partidas con estos filtros.</div>
          ) : null}
          </div>
          <footer className="xhm-footer">
            <span>
              {loading
                ? "Cargando…"
                : `Mostrando ${firstShown}–${lastShown} de ${formatNumber(list.total)} partidas`}
            </span>
            <span className="xhm-pages">
              <button
                type="button"
                className="xhm-page-arrow"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                aria-label="Página anterior"
              >
                ‹
              </button>
              {pages.map((entry, index) =>
                entry === "…" ? (
                  <span key={`gap-${index}`}>…</span>
                ) : (
                  <button
                    key={entry}
                    type="button"
                    className={entry === page ? "is-active" : ""}
                    onClick={() => setPage(entry)}
                  >
                    {entry}
                  </button>
                )
              )}
              <button
                type="button"
                className="xhm-page-arrow"
                disabled={page >= list.totalPages}
                onClick={() => setPage((current) => Math.min(list.totalPages, current + 1))}
                aria-label="Página siguiente"
              >
                ›
              </button>
            </span>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default HistoryModern;
